import React from 'react';
import { Box, Button, Chip, Grid, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface InvalidWaterMeter {
  index: number;
  name: string;
  diameter: number | null;
  q3: number | null;
  r: number | null;
  missingFields: string[];
}

type StatisticsErrorState =
  | {
      type: 'message';
      message: string;
      canManageConfig?: boolean;
      deviceNumber?: string;
      showSensorConfigAction?: boolean;
    }
  | {
      type: 'missing_config';
      invalidWaterMeters: InvalidWaterMeter[];
      canManageConfig: boolean;
      deviceNumber: string;
    };

const MissingWaterMeterConfigNotice: React.FC<StatisticsErrorStateProps> = ({
  error,
}: StatisticsErrorStateProps) => {
  const { t } = useTranslation();

  if (error.type === 'message') {
    return (
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          px: 3,
          py: 5,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('statistics_error_title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
          {error.message}
        </Typography>
        {error.showSensorConfigAction &&
          (error.canManageConfig && error.deviceNumber ? (
            <Box
              sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}
            >
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to={`/app/ParametersConfiguration/${error.deviceNumber}`}
              >
                {t('statistics_error_open_sensor_config')}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {t('statistics_error_update_values_hint')}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              {t('statistics_error_contact_admin')}
            </Typography>
          ))}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        p: { xs: 3, md: 4 },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {t('statistics_error_missing_config_title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, maxWidth: 960 }}>
        {t('statistics_error_missing_config_description')}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {error.invalidWaterMeters.map((waterMeter: InvalidWaterMeter) => (
          <Grid item xs={12} md={6} key={waterMeter.index}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                border: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {waterMeter.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {waterMeter.missingFields.map((field: string) => (
                    <Chip
                      key={field}
                      label={t('statistics_error_missing_field', { field })}
                      color="warning"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>

              <Grid container spacing={1.5} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FieldValue label={t('sensor_config_diameter')} value={waterMeter.diameter} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldValue label="q3" value={waterMeter.q3} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldValue label="r" value={waterMeter.r} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {error.canManageConfig ? (
        <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to={`/app/ParametersConfiguration/${error.deviceNumber}`}
          >
            {t('statistics_error_open_sensor_config')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {t('statistics_error_update_values_hint')}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          {t('statistics_error_contact_admin')}
        </Typography>
      )}
    </Paper>
  );
};

const FieldValue: React.FC<FieldValueProps> = ({ label, value }: FieldValueProps) => {
  const { t } = useTranslation();
  const hasValue: boolean = value !== null && value !== undefined;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'grey.50',
        px: 1.5,
        py: 1.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.75, fontWeight: 500 }}>
        {hasValue ? value : t('statistics_error_not_defined')}
      </Typography>
    </Box>
  );
};

interface FieldValueProps {
  label: string;
  value: number | null;
}

interface StatisticsErrorStateProps {
  error: StatisticsErrorState;
}

export type { InvalidWaterMeter, StatisticsErrorState };

export default MissingWaterMeterConfigNotice;
